import { Button } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useRootStore } from 'store/rootStore';
import { useHistory } from 'react-router-dom';
import { ProposalAction } from 'types/enrollmentService';
import ActionModal from 'components/common/modals/ActionModal';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { ModalType } from 'global/utils/ModalsHelper';
import { ProposalConfirmationStatus } from 'store/ui-stores/proposal-v2/proposalItemsStore';
import { ValidationError } from './ValidationError';
import { FooterStyled, AlertStyled, ButtonsWrapperStyled } from './styles';

const ProposalPageFooter = observer((): JSX.Element => {
  // React local state
  const [proposalActionModal, setProposalActionModal] = React.useState(false);
  const [swapLoading, setSwapLoading] = React.useState(false);

  // Mobx store
  const {
    proposalItemsStore: {
      loading,
      confirmProposalEnabled,
      proposalConfirmationStatus,
      enrollmentPeriod,
      validationErrors,
      rollbackEnrollment,
      confirmProposal,
      otherEnrollmentIsActiveForThisGroup,
    },
  } = useRootStore();

  // Common data
  const history = useHistory();

  // Component actions
  function redirect(url: string): void {
    history.push(url);
  }

  const onNewConfirm = async (): Promise<void> => {
    await confirmProposal(ProposalAction.CONFIRM, true);
    redirect(`/checklist/${enrollmentPeriod.id}`);
  };

  const onReConfirm = async (): Promise<void> => {
    await confirmProposal(ProposalAction.UPDATE, true);
    redirect(`/checklist/${enrollmentPeriod.id}`);
  };

  const onSwitchConfirm = async (): Promise<void> => {
    await confirmProposal(ProposalAction.SWITCH, true);
    redirect(`/checklist/${enrollmentPeriod.id}`);
  };

  const onSwapConfirm = async (): Promise<void> => {
    setSwapLoading(true);
    await Promise.all([rollbackEnrollment(), confirmProposal(ProposalAction.CONFIRM, true)]);
    setSwapLoading(false);
    redirect(`/checklist/${enrollmentPeriod.id}`);
  };

  const onExit = (): void => {
    redirect(`/group/${enrollmentPeriod.groupId}`);
  };

  const onSaveAndExit = async (): Promise<void> => {
    await confirmProposal(undefined, false);
    redirect(`/group/${enrollmentPeriod.groupId}`);
  };

  const goBackButton = (
    <Button
      size="large"
      type="outlined"
      width="24rem"
      onClick={(): void => {
        history.goBack();
      }}
    >
      Exit
    </Button>
  );

  // concomitant layout
  const FOOTER_LAYOUT = Object.freeze({
    [ProposalConfirmationStatus.NEW]: (
      <>
        <Button size="large" type="outlined" onClick={onSaveAndExit}>
          Save and Exit
        </Button>
        {confirmProposalEnabled ? (
          <Button size="large" color="primary" onClick={(): void => setProposalActionModal(true)}>
            Confirm Proposal
          </Button>
        ) : (
          <Button size="large" disabled>
            Confirm Proposal
          </Button>
        )}
        <ActionModal
          show={proposalActionModal}
          actionButtonText="Confirm Proposal"
          onClose={(): void => setProposalActionModal(false)}
          onAction={onNewConfirm}
          title="Are you sure you would like to proceed?"
          subtitle="This will move the group from the Quoting to Enrollment stage, and will kick-off the Enrollment Checklist. If you are ready, please click Confirm below."
          modalType={ModalType.NOTIFICATION}
          loading={loading}
        />
      </>
    ),
    [ProposalConfirmationStatus.CONFIRMED]: (
      <>
        <Button size="large" type="outlined" onClick={onExit}>
          Exit
        </Button>
        {confirmProposalEnabled ? (
          <Button size="large" color="primary" onClick={(): void => setProposalActionModal(true)}>
            Re-Confirm Proposal
          </Button>
        ) : (
          <Button size="large" disabled>
            Re-Confirm Proposal
          </Button>
        )}
        <ActionModal
          show={proposalActionModal}
          actionButtonText="Re-Confirm Proposal"
          onClose={(): void => setProposalActionModal(false)}
          onAction={onReConfirm}
          title="This enrollment is already in progress. Updating previously selected plans may result in lost enrollment progress."
          subtitle="Are you sure you would like to proceed?"
          modalType={ModalType.NOTIFICATION}
          loading={loading}
        />
      </>
    ),
    [ProposalConfirmationStatus.SWITCH]: (
      <>
        <Button size="large" type="outlined" onClick={onSaveAndExit}>
          Save and Exit
        </Button>
        {confirmProposalEnabled ? (
          <Button size="large" color="primary" onClick={(): void => setProposalActionModal(true)}>
            Switch Proposal
          </Button>
        ) : (
          <Button size="large" disabled>
            Switch Proposal
          </Button>
        )}
        <ActionModal
          show={proposalActionModal}
          actionButtonText="Switch Proposal"
          onClose={(): void => setProposalActionModal(false)}
          onAction={onSwitchConfirm}
          title={`You already have a confirmed proposal for
                ${dateFormatters.normDateToShortAmericanDate(enrollmentPeriod.effectiveDate)}. Switching proposals may
                result in lost enrollment progress.`}
          subtitle="Are you sure you would like to proceed?"
          modalType={ModalType.NOTIFICATION}
          loading={loading}
        />
      </>
    ),
    [ProposalConfirmationStatus.SWAP]: (
      <>
        <Button size="large" type="outlined" onClick={onSaveAndExit}>
          Save and Exit
        </Button>
        {confirmProposalEnabled ? (
          <Button size="large" color="primary" onClick={(): void => setProposalActionModal(true)}>
            Swap Proposal
          </Button>
        ) : (
          <Button size="large" disabled>
            Swap Proposal
          </Button>
        )}
        <ActionModal
          show={proposalActionModal}
          actionButtonText="Swap Proposal"
          onClose={(): void => setProposalActionModal(false)}
          onAction={onSwapConfirm}
          title={`You already have another active enrollment for
          ${
            otherEnrollmentIsActiveForThisGroup
              ? dateFormatters.normDateToShortAmericanDate(otherEnrollmentIsActiveForThisGroup.effectiveDate)
              : ''
          }. Swapping to
          this alternate proposal will result in the loss of any enrollment progress so far to ensure compliance with
          carrier rules. New effective date will be:
          ${dateFormatters.normDateToShortAmericanDate(enrollmentPeriod.effectiveDate)}
        `}
          subtitle="Are you sure you would like to proceed?"
          modalType={ModalType.NOTIFICATION}
          loading={swapLoading}
        />
      </>
    ),
    [ProposalConfirmationStatus.SUBMITTED]: (
      <>
        <Button size="large" type="outlined" onClick={onExit}>
          Exit
        </Button>
        {confirmProposalEnabled ? (
          <Button size="large" color="primary" onClick={(): void => setProposalActionModal(true)}>
            Update Proposal
          </Button>
        ) : (
          <Button size="large" disabled>
            Update Proposal
          </Button>
        )}
        <ActionModal
          show={proposalActionModal}
          actionButtonText="Update Proposal"
          onClose={(): void => setProposalActionModal(false)}
          onAction={onSwitchConfirm}
          title={`You already have a submitted enrollment period for
                ${dateFormatters.normDateToShortAmericanDate(enrollmentPeriod.effectiveDate)}. Updating proposals may
                result in lost enrollment progress.`}
          subtitle="Are you sure you would like to proceed?"
          modalType={ModalType.NOTIFICATION}
          loading={loading}
        />
      </>
    ),
    [ProposalConfirmationStatus.SOLD]: goBackButton,
    [ProposalConfirmationStatus.ARCHIVED]: goBackButton,
  });

  return (
    <FooterStyled $withValidationErrors={validationErrors.length > 0}>
      {!loading && validationErrors.length > 0 && (
        <AlertStyled
          title="Attention! Resolve the following issue(s) to continue"
          type="error"
          content={validationErrors.map((error) => (
            <ValidationError key={error.type} {...error} />
          ))}
        />
      )}
      <ButtonsWrapperStyled>{FOOTER_LAYOUT[proposalConfirmationStatus]}</ButtonsWrapperStyled>
    </FooterStyled>
  );
});

export default ProposalPageFooter;
